import clsx from 'clsx'
import { useState, useRef, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles'
import styles from './TwoFactorFieldStyles'

const useStyles = makeStyles(styles)

export default function TwoFactorInput({ digits = 6, onChange, onComplete, onIsCompleteChange, status: externalStatus }) {
  const classes = useStyles()
  const DIGITS = digits;
  const [values, setValues] = useState(Array(DIGITS).fill(""));
  const [internalStatus, setInternalStatus] = useState("idle"); // idle | loading | success | error
  const status = externalStatus ?? internalStatus;
  const [shake, setShake] = useState(false);
  const inputs = useRef([]);

  const focusIndex = (i) => {
    const el = inputs.current[Math.max(0, Math.min(i, DIGITS - 1))];
    if (el) el.focus();
  };

  useEffect(() => { focusIndex(0); }, []);

  useEffect(() => {
    if (status === "error") {
      triggerShake();
      setTimeout(() => {
        focusIndex(0);
        const next = Array(DIGITS).fill("");
        updateValues(next);
      }, 500);
    }
  }, [status]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const updateValues = (next) => {
    setValues(next);
    const code = next.join("");
    const complete = next.every(Boolean);
    onIsCompleteChange?.(complete);
    onChange?.(code);
    if (complete) onComplete?.(code);
  };

  const handleChange = (e, i) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) return;
    const char = raw[raw.length - 1];
    const next = [...values];
    next[i] = char;
    updateValues(next);
    if (i < DIGITS - 1) focusIndex(i + 1);
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...values];
      if (next[i]) {
        next[i] = "";
        updateValues(next);
      } else {
        focusIndex(i - 1);
        next[i - 1] = "";
        updateValues(next);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusIndex(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusIndex(i + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (!pasted) return;
    const next = Array(DIGITS).fill("");
    pasted.split("").forEach((c, i) => { next[i] = c; });
    updateValues(next);
    focusIndex(Math.min(pasted.length, DIGITS - 1));
  };

  const filled = values.filter(Boolean).length;
  const allFilled = filled === DIGITS;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0",
    }}>
      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)}
          60%{transform:translateX(-6px)}
          80%{transform:translateX(6px)}
        }
        @keyframes pop {
          0%{transform:scale(1)}
          50%{transform:scale(1.12)}
          100%{transform:scale(1)}
        }
        @keyframes fadeUp {
          from{opacity:0;transform:translateY(16px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes spin {
          to{transform:rotate(360deg)}
        }
      `}</style>

      <div className={classes['tfa-card']} style={{
        width: "100%",
        background: "var(--color-background-primary)",
        borderRadius: "20px",
        border: "0.5px solid var(--color-border-tertiary)",
        padding: "0",
        position: "relative",
        overflow: "hidden",
      }}>

        {/* Progress indicator */}
        <div className={classes['progress-bar']} style={{ width: `${(filled / DIGITS) * 100}%` }} />

        {/* Digit inputs */}
        <div
          style={{
            paddingTop: "5px",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "center",
            marginBottom: "1.75rem",
            animation: shake ? "shake 0.5s ease" : "none",
          }}
        >
          {values.map((v, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              className={clsx(classes['digit-input'], {
                filled: !!v,
                'success-input': status === "success",
                'error-input': status === "error",
              })}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={v}
              onChange={(e) => handleChange(e, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              disabled={status === "loading"}
              aria-label={`Digit ${i + 1} of ${DIGITS}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}